import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "../services/firebaseImports";
import { db } from "../config/firebase";
import Logger from "../services/logger";
import {
  LANDING_SUBSCRIBERS_COLLECTION,
  LANDING_SUBSCRIBER_DISCOUNT_CODE,
  LANDING_SUBSCRIBER_DISCOUNT_PERCENT,
} from "../constants/landingSubscriberPromo";
import { LANDING_VISUAL_THEME as tz } from "../constants/landingVisualTheme";

const subscribeUiGradient = `linear-gradient(135deg, ${tz.navyElevated} 0%, ${tz.navyCard} 100%)`;

const cardStyle: React.CSSProperties = {
  background: "transparent",
  borderRadius: 16,
  boxShadow: "none",
  padding: "24px 32px 32px 32px",
  maxWidth: 480,
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backdropFilter: "none",
  border: "none",
  width: "100%",
};

const inputStyle: React.CSSProperties = {
  padding: "12px 16px",
  borderRadius: 8,
  border: "1px solid rgba(255, 255, 255, 0.3)",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  color: "#fff",
  fontSize: 16,
  marginRight: 12,
  width: 220,
  outline: "none",
  marginBottom: 0,
};


const buttonStyle: React.CSSProperties = {
  padding: "12px 28px",
  borderRadius: 8,
  border: "none",
  background: subscribeUiGradient,
  color: "#fff",
  fontWeight: 700,
  fontSize: 16,
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "background 0.2s",
};

const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalCard: React.CSSProperties = {
  background: subscribeUiGradient,
  padding: 32,
  borderRadius: 16,
  minWidth: 340,
  maxWidth: 480,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  position: 'relative',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  margin: '18px 0 8px',
  fontWeight: 500,
  color: '#fff',
};

const textareaStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  minHeight: 48,
  borderRadius: 8,
  border: '1px solid rgba(255, 255, 255, 0.3)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#fff',
  padding: '10px 12px',
  fontSize: 15,
  marginTop: 4,
  resize: 'vertical',
};

const selectStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  borderRadius: 8,
  border: '1px solid rgba(255, 255, 255, 0.3)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#fff',
  padding: '10px 12px',
  fontSize: 15,
  marginTop: 4,
};

const SubscribeWithSurveyNew: React.FC = () => {
  const [email, setEmail] = useState("");
  const [showSurvey, setShowSurvey] = useState(false);
  const [answers, setAnswers] = useState({
    name: "",
    phoneNumber: "",
    role: "",
    concern: "",
    tech: "",
  });
  const [surveyStatus, setSurveyStatus] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowSurvey(true);
    setSurveyStatus("");
  };

  const handleSurveyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setAnswers({ ...answers, [e.target.name]: e.target.value });
  };

  const handleSurveySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSurveyStatus("Submitting...");
    
    try {
      // Save to Firestore with all survey data
      // The Firestore trigger (onSubscriberCreate) will automatically send emails
      await addDoc(collection(db, LANDING_SUBSCRIBERS_COLLECTION), {
        email,
        name: answers.name,
        phoneNumber: answers.phoneNumber || null,
        role: answers.role,
        concern: answers.concern,
        tech: answers.tech,
        createdAt: serverTimestamp(),
        discountCode: LANDING_SUBSCRIBER_DISCOUNT_CODE,
        discountPercentage: LANDING_SUBSCRIBER_DISCOUNT_PERCENT,
        emailSent: false,
      });
      
      setSurveyStatus(
        `Thank you! Your ${LANDING_SUBSCRIBER_DISCOUNT_PERCENT}% discount code is ${LANDING_SUBSCRIBER_DISCOUNT_CODE}. Save it for when we launch!`
      );
      
      setTimeout(() => {
        setShowSurvey(false);
        setEmail("");
        setAnswers({ name: "", phoneNumber: "", role: "", concern: "", tech: "" });
        setSurveyStatus("");
      }, 4000);
      
    } catch (err: any) {
      Logger.error('Survey submission error:', err instanceof Error ? err : new Error(String(err)), { err });
      setSurveyStatus("There was an error saving your submission. Please try again.");
    }
  };

  return (
    <>
      <style>{`
        input::placeholder,
        textarea::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        select {
          color: #fff !important;
        }
        select option {
          background: ${tz.navy};
          color: #fff;
        }
      `}</style>
      <div style={{ width: '100%', background: 'transparent', padding: '0' }}>
        <div style={cardStyle}>
        <h2 style={{ fontWeight: 800, fontSize: 28, marginBottom: 16, color: '#fff', textAlign: 'center', letterSpacing: '0.5px' }}>Stay Connected</h2>
        <p style={{ color: '#fff', marginBottom: 32, fontSize: 17, textAlign: 'center', opacity: 0.9, lineHeight: '1.7', maxWidth: '90%', marginLeft: 'auto', marginRight: 'auto' }}>
          Get updates and be the first to know when Tranzit.io goes live.<br />
          <span style={{ color: '#fff', fontWeight: 600, display: 'block', marginTop: '8px' }}>Answer 3 questions after subscribing for an exclusive discount!</span>
        </p>
        <form style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', marginBottom: 0, gap: '12px' }} onSubmit={handleSubscribe}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            style={inputStyle}
          />
          <button type="submit" style={buttonStyle}>Subscribe</button>
        </form>
      </div>

      {showSurvey && (
        <div className="modal" style={modalOverlay}>
          <form onSubmit={handleSurveySubmit} style={modalCard}>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowSurvey(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 20,
                background: 'none',
                border: 'none',
                fontSize: 24,
                color: '#fff',
                cursor: 'pointer',
                zIndex: 10,
                fontWeight: 700,
              }}
            >
              ×
            </button>
            <h3 style={{ fontWeight: 700, fontSize: 22, marginBottom: 8, color: '#fff' }}>Thank you for subscribing!</h3>
            <p style={{ color: '#fff', marginBottom: 18, fontSize: 16, opacity: 0.9 }}>
              Answer 3 quick questions for an <span style={{ color: '#fff', fontWeight: 600 }}>exclusive discount</span>:
            </p>
            
            <label style={labelStyle}>
              Full Name *
              <input 
                name="name" 
                value={answers.name} 
                onChange={handleSurveyChange} 
                required 
                style={{ ...inputStyle, width: '100%', marginRight: 0, marginBottom: 0 }}
                placeholder="Enter your full name"
              />
            </label>
            
            <label style={labelStyle}>
              Phone Number (Optional)
              <input 
                name="phoneNumber" 
                value={answers.phoneNumber} 
                onChange={handleSurveyChange} 
                type="tel"
                style={{ ...inputStyle, width: '100%', marginRight: 0, marginBottom: 0 }}
                placeholder="Enter your phone number"
              />
            </label>
            
            <label style={labelStyle}>
              Are you a shipper, carrier, broker, or broker/carrier?
              <select name="role" value={answers.role} onChange={handleSurveyChange} required style={selectStyle}>
                <option value="">Select...</option>
                <option value="shipper">Shipper</option>
                <option value="carrier">Carrier</option>
                <option value="broker">Broker</option>
                <option value="broker_carrier">Broker/Carrier</option>
              </select>
            </label>
            
            <label style={labelStyle}>
              What is your biggest concern in working directly with your shipper/carrier counterpart?
              <textarea name="concern" value={answers.concern} onChange={handleSurveyChange} required style={textareaStyle} />
            </label>
            
            <label style={labelStyle}>
              What can we do from the tech side to make your operations easier between you and your partner?
              <textarea name="tech" value={answers.tech} onChange={handleSurveyChange} required style={textareaStyle} />
            </label>
            
            <button type="submit" style={{ ...buttonStyle, width: '100%', marginTop: 18 }}>Submit for Discount</button>
            
            {surveyStatus && (
              <div style={{ 
                marginTop: 16, 
                padding: '12px 16px', 
                borderRadius: 8, 
                background: surveyStatus.includes('error') ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 255, 0, 0.2)',
                color: '#fff',
                border: surveyStatus.includes('error') ? '1px solid rgba(255, 0, 0, 0.5)' : '1px solid rgba(0, 255, 0, 0.5)',
                fontSize: 14,
                textAlign: 'center',
                fontWeight: 500
              }}>
                {surveyStatus}
              </div>
            )}
          </form>
        </div>
      )}
      </div>
    </>
  );
};

export default SubscribeWithSurveyNew;
